import { Link, makeStyles, tokens } from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import { navigateToWordComment } from '../../../api/word/word-service'
import { useNotification } from '../../../hooks/useNotification'
import {
  sanitizeAssistantHtmlForBubble,
  scopeEmbeddedStyles,
} from '../../../utils/html-sanitization'

/** One segment per comment, index-aligned with wordCommentIds. */
function splitReviewSummarySegments(content: string): string[] {
  return content
    .split(/(?:<br\s*\/?>\s*){2,}|\n+/i)
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0)
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  navigateButton: {
    fontSize: 'inherit',
  },
  separator: {
    margin: `0 ${tokens.spacingHorizontalS}`,
  },
})

interface CommentSummaryContentProps {
  content: string
  wordCommentIds: (string | null)[]
  bubbleScopeId: string
}

export const CommentSummaryContent = ({
  content,
  wordCommentIds,
  bubbleScopeId,
}: CommentSummaryContentProps) => {
  const styles = useStyles()
  const { t } = useTranslation()
  const { notify } = useNotification()
  const segments = splitReviewSummarySegments(content)

  const handleNavigate = async (commentId: string) => {
    try {
      await navigateToWordComment(commentId)
    } catch {
      notify({ isSuccess: false, text: t('chat.commentNavigationFailed') })
    }
  }

  return (
    <div className={styles.root}>
      {segments.map((segment, index) => {
        const commentId = wordCommentIds[index]
        const sanitizedHtml = scopeEmbeddedStyles(
          sanitizeAssistantHtmlForBubble(segment),
          bubbleScopeId
        )

        return (
          <p key={commentId ? `comment-${commentId}` : `summary-${index}`}>
            <span
              // eslint-disable-next-line react-dom/no-dangerously-set-innerhtml
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
            {commentId ? (
              <>
                <span className={styles.separator}>&#183;</span>
                <Link
                  as="button"
                  inline
                  className={styles.navigateButton}
                  aria-label={t('chat.viewCommentInDocumentAriaLabel', { number: index + 1 })}
                  onClick={() => handleNavigate(commentId)}>
                  {t('chat.viewCommentInDocument')}
                </Link>
              </>
            ) : null}
          </p>
        )
      })}
    </div>
  )
}
